import {useAuth} from "@/app/auth/hooks";
import {UserRole} from "@/lib/types";
import {noOp} from "@/lib/utils";
import {useFeatureFlags} from "@/hooks/use-api-endpoint";

export function useUserRole(): UserRole | null {
  const {status} = useAuth();
  const {userRole, isLoading} = useFeatureFlags();

  // Return null if not authenticated or still loading
  if (status !== "authenticated" || isLoading) {
    return null;
  }

  return userRole || null;
}

// a key-value map of user roles to option functions, including the 'unauthenticated' state
// all options must be provided, even if they are no-ops. Otherwise the app will not compile (this is intentional)
type UserRoleOptionsMap<T> = { [key in UserRole]: (...args: any[]) => T }
  & { 'unauthenticated': (...args: any[]) => T };

/**
 * From a list of options, return the option that matches the user's highest precedent role.
 * The options can be strings, components, functions, or any other type.
 *
 * Component usage example:
 *
 *    const roleOption = useUserRoleOptions({
 *      [UserRole.ADMIN]: () => "YOU ARE AN ADMIN",
 *      [UserRole.ALPHA]: (props) => <AlphaComponent {...props} />,
 *      [UserRole.BETA]: (props) => <BetaComponent {...props} />,
 *      [UserRole.DEFAULT_USER]: () => console.log("User does not have a role, no component will be shown"),
 *      ['unauthenticated']: () => <LoadingSpinner/>,
 *    });
 *
 *    return (
 *      <div>
 *        {roleOption({ id: "some-id" , text: "some-text" })}
 *      </div>
 *    );
 */
export function useUserRoleOption<T>(options: UserRoleOptionsMap<T>) {
  const userRole = useUserRole();
  return options[userRole || 'unauthenticated'];
}

interface UserRoleSwitchProps<P> {
  components: UserRoleOptionsMap<Element | typeof noOp>; // React components or no-op functions
  componentProps: P
}

/**
 * A component that switches between different components based on the user's role.
 *
 * Usage example:
 *
 *    <UserRoleSwitch
 *      components={{
 *        [UserRole.ADMIN]: AdminComponent,
 *        [UserRole.ALPHA]: AlphaComponent,
 *        [UserRole.BETA]: noOp, // no-op, do not render anything for Beta users
 *        [UserRole.DEFAULT_USER]: DefaultUserComponent,
 *        ['unauthenticated']: () => console.warn("User is not authenticated, no component will be shown"),
 *      }}
 *      componentProps={{ someProp: "someValue", anotherProp: 123 }}
 *    />
 */
export default function UserRoleSwitch<P extends object>({components, componentProps}: UserRoleSwitchProps<P>) {
  // get the component for the user's role
  const roleComponent = useUserRoleOption(components);

  // render the component with the provided props. Render nothing (e.g. return null) if no component provided for the option.
  return roleComponent(componentProps) || null;
}